import React, { useContext, useEffect } from "react";

import { Box, Button } from "@mui/material";
import { debounce } from "lodash";
import { reaction } from "mobx";
import { observer } from "mobx-react-lite";
import { makeStyles } from "tss-react/mui";

import useKeyboardShortcut from "@/hooks/useKeyboardShortcut";
import { colors } from "@/theme/overrides/colors";

import BackToButton from "./BackToButton";
import ClipboardSnackbar from "./ClipboardSnackbar";
import DayHeadersRow from "./DayHeadersRow";
import Header from "./Header";
import PlannedMealRows from "./PlannedMealRows";
import MealPlanFormStore from "../store/MealPlanFormStore";

const useStyles = makeStyles()((theme) => ({
  table: {
    overflowX: "auto",
    border: `${theme.spacing(1)} solid #fafafa`,
  },
  headerRow: {
    display: "flex",
    width: "max-content", // for sticky row headers to work all the way
  },
  headerCell: {
    border: "2px solid #fafafa",
    minWidth: 400,
    maxWidth: 400,
    backgroundColor: colors.white,
  },
  row: {
    position: "relative",
    display: "flex",
    alignItems: "stretch",
    width: "max-content", // for sticky row headers to work all the way
    minHeight: 200,
  },
  rowHeader: {
    position: "sticky",
    left: 0,
    minWidth: 125,
    maxWidth: 125,
    border: "2px solid #fafafa",
    backgroundColor: colors.white,
    zIndex: 10,
  },
  cell: {
    minWidth: 400,
    maxWidth: 400,
  },
}));

interface Props {
  backButtonTo: string;
  backButtonLabel: string;
  onAutoSave: () => void;
  onManualSave: () => void;
}

export const MealPlannerForm: React.FC<Props> = observer(
  ({ backButtonTo, backButtonLabel, onAutoSave, onManualSave }) => {
    const { classes } = useStyles();

    const store = useContext(MealPlanFormStore.Context);

    useKeyboardShortcut("Ctrl+S", () => onManualSave());

    useKeyboardShortcut("Escape", () => store.cells.clearActive());

    useKeyboardShortcut("ArrowUp", () => {
      const cell = store.cells.up();
      document
        .querySelector(`[data-cell="${cell.key}"]`)
        ?.scrollIntoView({ block: "nearest", inline: "center" });
    });

    useKeyboardShortcut("ArrowDown", () => {
      const cell = store.cells.down();
      document
        .querySelector(`[data-cell="${cell.key}"]`)
        ?.scrollIntoView({ block: "nearest", inline: "center" });
    });

    useKeyboardShortcut("ArrowLeft", () => {
      const cell = store.cells.left();
      document
        .querySelector(`[data-cell="${cell.key}"]`)
        ?.scrollIntoView({ block: "nearest", inline: "center" });
    });

    useKeyboardShortcut("ArrowRight", () => {
      const cell = store.cells.right();
      document
        .querySelector(`[data-cell="${cell.key}"]`)
        ?.scrollIntoView({ block: "nearest", inline: "center" });
    });

    const autoSave = debounce(() => onAutoSave(), 2000);
    useEffect(() => {
      const disposer = reaction(
        () => store.toAttributes(),
        () => autoSave()
      );
      return () => disposer();
    }, []);

    return (
      <>
        <Header />
        <div className={classes.table}>
          <DayHeadersRow />
          <PlannedMealRows />
        </div>
        <Box padding={4} />
        <Box
          position="relative"
          display="flex"
          justifyContent="center"
          alignItems="center"
          width="100%"
        >
          <Box position="absolute" left={0}>
            <BackToButton to={backButtonTo} label={backButtonLabel} />
          </Box>
          <Button variant="outlined" onClick={() => store.previousStep()}>
            Back
          </Button>
          <Box padding={1} />
          <Button onClick={() => onManualSave()}>Save</Button>
        </Box>
        <ClipboardSnackbar />
      </>
    );
  }
);

export default MealPlannerForm;
